/**
 * Image caching utility using browser Cache API
 * Caches images in the browser for faster subsequent loads
 */

const CACHE_NAME = 'jav-portfolio-images-v1'
const CACHE_EXPIRATION = 7 * 24 * 60 * 60 * 1000 // 7 days

interface CachedImage {
  url: string
  timestamp: number
}

export const imageCache = {
  /**
   * Initialize the cache
   */
  init: async (): Promise<void> => {
    if (!('caches' in window)) {
      console.warn('Cache API not supported')
      return
    }

    try {
      // Clean up old cache entries
      const cache = await caches.open(CACHE_NAME)
      const requests = await cache.keys()
      const now = Date.now()

      for (const request of requests) {
        const response = await cache.match(request)
        if (response) {
          const cachedTime = response.headers.get('x-cached-time')
          if (cachedTime && now - parseInt(cachedTime) > CACHE_EXPIRATION) {
            await cache.delete(request)
          }
        }
      }
    } catch (error) {
      console.error('Cache init error:', error)
    }
  },

  /**
   * Preload and cache an image
   */
  preload: async (url: string): Promise<void> => {
    if (!('caches' in window)) return

    try {
      const cache = await caches.open(CACHE_NAME)

      // Check if already cached
      const cached = await cache.match(url)
      if (cached) return

      // Fetch and cache the image
      const response = await fetch(url, {
        mode: 'cors',
        credentials: 'omit',
      })

      if (response.ok) {
        // Clone response and add timestamp header
        const clonedResponse = new Response(response.body, {
          status: response.status,
          statusText: response.statusText,
          headers: new Headers(response.headers),
        })
        clonedResponse.headers.set('x-cached-time', Date.now().toString())

        await cache.put(url, clonedResponse)
      }
    } catch (error) {
      console.error('Image preload error:', error)
    }
  },

  /**
   * Preload multiple images
   */
  preloadBatch: async (urls: string[]): Promise<void> => {
    const promises = urls.map((url) => imageCache.preload(url))
    await Promise.all(promises)
  },

  /**
   * Get cached image URL (returns original URL, browser handles cache)
   */
  get: (url: string): string => {
    // Browser automatically uses cached version if available
    return url
  },

  /**
   * Clear image cache
   */
  clear: async (): Promise<void> => {
    if (!('caches' in window)) return

    try {
      await caches.delete(CACHE_NAME)
    } catch (error) {
      console.error('Cache clear error:', error)
    }
  },

  /**
   * Get cache size (approximate)
   */
  getSize: async (): Promise<number> => {
    if (!('caches' in window)) return 0

    try {
      const cache = await caches.open(CACHE_NAME)
      const requests = await cache.keys()
      return requests.length
    } catch (error) {
      console.error('Cache size error:', error)
      return 0
    }
  },
}

/**
 * Optimize image URL for better caching
 * Adds cache-busting parameters if needed
 */
export const optimizeImageUrl = (url: string): string => {
  // If URL is from Supabase storage, it already has good caching headers
  if (url.includes('supabase.co/storage')) {
    return url
  }

  // For external URLs, ensure proper caching
  return url
}
